import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './CategoryRail.css';
import { ChannelCardSkeleton } from './common/SkeletonLoaders';
import ChannelCard from './ChannelCard';
import ThumbnailHoverModal from './ThumbnailHoverModal';
import PDPModal from './PDPModal';
import { useCategoryRail } from '../hooks/useCategoryRail';
import { getSlidesToShow, RAIL_BREAKPOINTS } from '../config/categoryRailSettings';
import { preloadSlimEventDataForChannels } from '../utils/channelDateUtils';

const CategoryRail = React.memo(({ 
  category,
  preCalculatedContinueWatching = [],
  preCalculatedWatchAgain = [],
  forceTextTitle = false,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const [hoveredCardIndex, setHoveredCardIndex] = useState(-1);

  const [pdpModalData, setPdpModalData] = useState({
    isVisible: false,
    channel: null,
    videoState: { autoPlay: false, currentTime: 0 },
  });
  const [, setCmsHydrationTick] = useState(0);

  const railRef = useRef(null);
  const navigate = useNavigate();

  const [localModalState, setLocalModalState] = useState({
    isVisible: false,
    channel: null,
    position: null,
    index: -1,
  });

  const hideTimeoutRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasBeenVisible) {
          setIsVisible(true);
          setHasBeenVisible(true);
        }
      },
      { root: null, rootMargin: '200px', threshold: 0.1 },
    );

    if (railRef.current) {
      const currentRef = railRef.current;
      observer.observe(currentRef);
      return () => observer.unobserve(currentRef);
    }
  }, [hasBeenVisible]);

  const { channels, loading, error, retry } = useCategoryRail(category, isVisible);

  useEffect(() => {
    let isMounted = true;

    const preloadSlimEventsForRail = async () => {
      if (!channels?.length) return;
      const preloadedCount = await preloadSlimEventDataForChannels(channels);
      if (isMounted && preloadedCount > 0) setCmsHydrationTick(v => v + 1);
    };

    preloadSlimEventsForRail();

    return () => {
      isMounted = false;
    };
  }, [channels]);

  const applyNeighborDisplacement = useCallback(hoveredIndex => {
    if (!railRef.current) return;
    const sliderTrack = railRef.current.querySelector('.slick-track');
    if (!sliderTrack) return;
    const cards = sliderTrack.querySelectorAll('.channel-card-container');

    cards.forEach((card, index) => {
      if (index === hoveredIndex) return;
      const distance = index - hoveredIndex;
      const direction = distance > 0 ? 1 : -1;
      const absDistance = Math.abs(distance);
      let displacement = 0;
      if (absDistance === 1) displacement = direction * 60;
      else if (absDistance === 2) displacement = direction * 30;
      card.style.transform = displacement ? `translateX(${displacement}px)` : '';
      card.style.transition = 'transform 0.3s ease';
    });
  }, []);

  const resetNeighborDisplacement = useCallback(() => {
    if (!railRef.current) return;
    const sliderTrack = railRef.current.querySelector('.slick-track');
    if (!sliderTrack) return;
    const cards = sliderTrack.querySelectorAll('.channel-card-container');
    cards.forEach(card => { card.style.transform = ''; });
  }, []);

  const handleHoverModalShow = useCallback(
    (channel, position, index) => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      const railRect = railRef.current?.getBoundingClientRect();
      const adjustedPosition = railRect
        ? { x: position.x - railRect.left, y: position.y - railRect.top }
        : position;
      setLocalModalState({ isVisible: true, channel, position: adjustedPosition, index });
      setHoveredCardIndex(index);
      applyNeighborDisplacement(index);
    },
    [applyNeighborDisplacement],
  );

  const handleHoverModalHide = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    hideTimeoutRef.current = setTimeout(() => {
      setLocalModalState({ isVisible: false, channel: null, position: null, index: -1 });
      resetNeighborDisplacement();
      setHoveredCardIndex(-1);
      hideTimeoutRef.current = null;
    }, 300);
  }, [resetNeighborDisplacement]);

  const handleModalMouseEnter = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const handleModalMouseLeave = useCallback(() => {
    handleHoverModalHide();
  }, [handleHoverModalHide]);

  const handleOpenPDPModal = useCallback(
    videoState => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      setLocalModalState({ isVisible: false, channel: null, position: null, index: -1 });
      resetNeighborDisplacement();
      setHoveredCardIndex(-1);
      setPdpModalData({
        isVisible: true,
        channel: localModalState.channel,
        videoState: videoState || { autoPlay: false, currentTime: 0 },
      });
    },
    [localModalState.channel, resetNeighborDisplacement],
  );

  const handleClosePDPModal = useCallback(() => {
    setPdpModalData({
      isVisible: false,
      channel: null,
      videoState: { autoPlay: false, currentTime: 0 },
    });
  }, []);

  const handleHoverModalHideImmediate = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setLocalModalState({ isVisible: false, channel: null, position: null, index: -1 });
    resetNeighborDisplacement();
    setHoveredCardIndex(-1);
  }, [resetNeighborDisplacement]);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    };
  }, []);

  const handleSeeAllClick = () => {
    if (!category) return;
    if (category._id === 'continue-watching' || category.style === 'continue-watching') {
      navigate('/my-lists/continue-watching');
      return;
    }
    const identifier = category.slug || category._id;
    if (identifier) navigate(`/category/${identifier}`);
  };

  if (!category) {
    return (
      <div className="my-8 category-rail" ref={railRef}>
        <div className="px-2 text-white/50">Category data unavailable</div>
      </div>
    );
  }

  const title = category.display_name || category.name || 'Untitled Category';

  const getCurrentSlidesToShow = () => {
    const width = window.innerWidth;
    const type = category?.style === 'top-number-poster' ? 'poster' : 'regular';
    return getSlidesToShow(width, type);
  };

  const shouldShowSeeAll = () => {
    if (!channels || channels.length === 0) return false;
    if (forceTextTitle) return true;
    const visibleCount = Math.floor(getCurrentSlidesToShow());
    return channels.length > visibleCount;
  };

  return (
    <div
      className={`category-rail relative ${
        channels.length <= Math.floor(getCurrentSlidesToShow()) ? 'rail-insufficient-items' : ''
      }`}
      ref={railRef}
    >
      <div className="flex items-center justify-between mb-4">
        {/* Category logo or text title */}
        {category.logo ? (
          <div className="category-logo-container">
            <img
              src={`${category.logo}/300/50`}
              alt={title}
              className="category-logo h-8 md:h-10 lg:h-12 max-w-[200px] md:max-w-[250px] lg:max-w-[300px] object-contain"
              srcSet={`
                ${category.logo}/120/20 120w,
                ${category.logo}/180/30 180w,
                ${category.logo}/240/40 240w,
                ${category.logo}/300/50 300w,
                ${category.logo}/360/60 360w,
                ${category.logo}/420/70 420w
              `}
              sizes="(max-width: 767px) 120px, (max-width: 1023px) 180px, (max-width: 1366px) 240px, 300px"
              onError={(e) => {
                e.target.style.display = 'none';
                if (e.target.nextElementSibling) e.target.nextElementSibling.style.display = 'block';
              }}
            />
            <h2
              className={`font-bold text-white font-display uppercase ${
                forceTextTitle ? 'mt-2 block' : 'hidden'
              }`}
            >
              {title}
            </h2>
          </div>
        ) : (
          <h2 className="font-bold text-white font-display uppercase">{title}</h2>
        )}
        {shouldShowSeeAll() && (
          <button className="text-sm text-white/70 hover:text-white" onClick={handleSeeAllClick}>
            See All
          </button>
        )}
      </div>

      {error ? (
        <div className="flex flex-col items-center justify-center w-full py-4">
          <div className="mb-2 text-red-400">{error}</div>
          <button
            onClick={retry}
            className="px-4 py-1 text-sm text-white rounded-full bg-accent/80 hover:bg-accent"
          >
            Retry
          </button>
        </div>
      ) : (
        <div>
          <Slider
            dots={false}
            infinite={!hasBeenVisible || loading ? false : channels.length > 5}
            speed={500}
            slidesToShow={getSlidesToShow(
              window.innerWidth,
              category?.style === 'top-number-poster' ? 'poster' : 'regular',
            )}
            slidesToScroll={3}
            arrows={!hasBeenVisible || loading ? false : true}
            responsive={RAIL_BREAKPOINTS.map(bp => ({
              breakpoint: bp.breakpoint,
              settings: {
                slidesToShow: bp[category?.style === 'top-number-poster' ? 'poster' : 'regular'],
                slidesToScroll: 1,
              },
            }))}
          >
            {(() => {
              if (!hasBeenVisible || loading) {
                const getSkeletonCount = () => {
                  const width = window.innerWidth;
                  const type = category?.style || 'regular';
                  return Math.ceil(getSlidesToShow(width, type));
                };

                return Array.from({ length: getSkeletonCount() }).map((_, index) => (
                  <div key={`skeleton-${index}`} className="px-2">
                    <ChannelCardSkeleton isPosterFormat={category?.style === 'top-number-poster'} />
                  </div>
                ));
              } else {
                return channels.map((channel, index) => (
                  <div key={channel?._id || index} className="px-2">
                    <ChannelCard
                      channel={channel || {}}
                      style={category?.style}
                      index={index}
                      onHoverModalShow={handleHoverModalShow}
                      onHoverModalHide={handleHoverModalHide}
                      onCardClick={handleHoverModalHideImmediate}
                    />
                  </div>
                ));
              }
            })()}
          </Slider>
        </div>
      )}
      {localModalState.isVisible && localModalState.channel && (
        <ThumbnailHoverModal
          channel={localModalState.channel}
          isVisible={true}
          position={localModalState.position}
          onMouseEnter={handleModalMouseEnter}
          onMouseLeave={handleModalMouseLeave}
          onOpenPDPModal={handleOpenPDPModal}
          preCalculatedContinueWatching={preCalculatedContinueWatching}
          preCalculatedWatchAgain={preCalculatedWatchAgain}
        />
      )}
      {pdpModalData.isVisible && (
        <PDPModal
          channel={pdpModalData.channel}
          isVisible={pdpModalData.isVisible}
          onClose={handleClosePDPModal}
          autoPlayVideo={pdpModalData.videoState.autoPlay}
          videoCurrentTime={pdpModalData.videoState.currentTime}
        />
      )}
    </div>
  );
});

export default CategoryRail;
